import { useState } from "react";
import { cn } from "@/lib/utils";
import { Plus, Clock, Info, Coins } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  HU_DAY_LABELS,
  colorStyles,
  fmtHours,
} from "@/pages/schedule/helpers";
import DraftBlock from "@/pages/schedule/DraftBlock";

// ---------------------------------------------------------------------------
// ScheduleGrid — Apple/Stripe aesthetic
// - Sticky day-header (today: dátum kör indigo háttéren)
// - Sticky left employee column
// - Weekend subtle tint
// - Shift blocks: tinted surface + left accent stripe
// - Draft blocks: dashed border, with inline approve/reject buttons
// - Empty cell + button → popover with template picker (NO drag-and-drop)
// ---------------------------------------------------------------------------
export default function ScheduleGrid({
  section,
  dates,
  templates = [],
  onAssignTemplate,
  onShiftClick,
  onApproveDraft,
  onRejectDraft,
  approvingDraftId,
  selectedIds,
  onToggleSelect,
}) {
  const employees = section.employees || [];
  const todayIso = new Date().toISOString().slice(0, 10);

  // CSS grid template: 220px employee col + 7 day cols (minmax 108px..1fr)
  const gridCols = "220px repeat(7, minmax(108px, 1fr))";

  return (
    <div
      data-testid={`schedule-grid-${section.site_id}`}
      className="relative overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-[0_8px_30px_-18px_rgba(15,23,42,0.16)] dark:border-border dark:bg-card"
    >
      {/* Subtle dot-matrix background to kill flat-screen feel */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.04] [background-image:radial-gradient(circle_at_1px_1px,_currentColor_1px,_transparent_0)] [background-size:18px_18px]"
        aria-hidden
      />

      <div className="relative max-h-[78vh] overflow-auto">
        {/* HEADER ROW */}
        <div
          className="sticky top-0 z-30 grid bg-white/85 backdrop-blur-md dark:bg-card/90"
          style={{ gridTemplateColumns: gridCols }}
        >
          {/* Corner cell (sticky top-left) */}
          <div className="sticky left-0 z-40 flex items-end border-b border-r border-slate-200/80 bg-white/95 px-4 py-3 backdrop-blur-md dark:border-border dark:bg-card/95">
            <span className="text-[10.5px] font-semibold uppercase tracking-[0.18em] text-slate-400 dark:text-muted-foreground">
              Dolgozó
            </span>
          </div>
          {dates.map((d, i) => {
            const isToday = d === todayIso;
            const isWeekend = i >= 5;
            const dt = new Date(d + "T00:00:00");
            const dayNum = String(dt.getDate()).padStart(2, "0");
            const stats = (section.day_stats || {})[d] || { hours: 0, revenue: 0 };
            return (
              <div
                key={d}
                className={cn(
                  "relative flex flex-col items-center justify-center border-b border-slate-200/80 px-3 py-3 dark:border-border",
                  isWeekend && "bg-slate-50/60 dark:bg-foreground/[0.025]",
                  i < dates.length - 1 && "border-r border-slate-100 dark:border-border/60",
                  "border-r border-slate-100 dark:border-border/60",
                )}
              >
                {/* Info-icon top-right with hover tooltip */}
                <DayInfoIcon stats={stats} />
                <span
                  className={cn(
                    "text-[10px] font-semibold uppercase tracking-[0.22em]",
                    isToday
                      ? "text-indigo-600 dark:text-indigo-400"
                      : isWeekend
                        ? "text-slate-400 dark:text-muted-foreground/70"
                        : "text-slate-400 dark:text-muted-foreground",
                  )}
                >
                  {HU_DAY_LABELS[i].long}
                </span>
                {isToday ? (
                  <span
                    className="mt-1 grid h-8 w-8 place-items-center rounded-full bg-indigo-600 text-[13px] font-semibold text-white shadow-[0_4px_10px_-3px_rgba(79,70,229,0.45)]"
                    aria-label="Ma"
                  >
                    {dayNum}
                  </span>
                ) : (
                  <span
                    className={cn(
                      "mt-1 grid h-8 w-8 place-items-center text-[18px] font-light tracking-tight",
                      isWeekend
                        ? "text-slate-400 dark:text-muted-foreground/80"
                        : "text-slate-800 dark:text-foreground",
                    )}
                  >
                    {dayNum}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* BODY ROWS */}
        {employees.length === 0 ? (
          <div className="grid place-items-center px-6 py-14 text-center">
            <p className="text-sm text-slate-500 dark:text-muted-foreground">
              Ehhez a telephelyhez nincs hozzárendelt dolgozó.
            </p>
          </div>
        ) : (
          employees.map((emp) => (
            <EmployeeRow
              key={emp._id}
              employee={emp}
              dates={dates}
              todayIso={todayIso}
              gridCols={gridCols}
              templates={templates}
              onAssignTemplate={onAssignTemplate}
              onShiftClick={onShiftClick}
              onApproveDraft={onApproveDraft}
              onRejectDraft={onRejectDraft}
              approvingDraftId={approvingDraftId}
              selectedIds={selectedIds}
              onToggleSelect={onToggleSelect}
            />
          ))
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function EmployeeRow({
  employee,
  dates,
  todayIso,
  gridCols,
  templates,
  onAssignTemplate,
  onShiftClick,
  onApproveDraft,
  onRejectDraft,
  approvingDraftId,
  selectedIds,
  onToggleSelect,
}) {
  const shiftsByDate = {};
  (employee.shifts || []).forEach((sh) => {
    shiftsByDate[sh.date] = shiftsByDate[sh.date] || [];
    shiftsByDate[sh.date].push(sh);
  });
  const draftsByDate = {};
  (employee.drafts || []).forEach((dr) => {
    draftsByDate[dr.date] = draftsByDate[dr.date] || [];
    draftsByDate[dr.date].push(dr);
  });

  return (
    <div
      className="group/row grid"
      style={{ gridTemplateColumns: gridCols }}
      data-testid={`emp-row-${employee._id}`}
    >
      {/* Sticky employee cell */}
      <div className="sticky left-0 z-20 flex items-center gap-3 border-b border-r border-slate-200/80 bg-white/95 px-4 py-2 backdrop-blur-sm transition-colors group-hover/row:bg-slate-50/60 dark:border-border dark:bg-card/95 dark:group-hover/row:bg-foreground/[0.03]">
        <Avatar name={employee.name} />
        <div className="min-w-0 flex-1">
          <p className="truncate text-[13px] font-medium leading-tight text-slate-900 dark:text-foreground">
            {employee.name}
          </p>
          {employee.position && (
            <p className="mt-0.5 truncate text-[10.5px] text-slate-500 dark:text-muted-foreground">
              {employee.position}
            </p>
          )}
        </div>
        <HoursRing hours={employee.weekly_hours} testId={`emp-weekly-${employee._id}`} />
      </div>

      {/* Day cells */}
      {dates.map((d, i) => {
        const isWeekend = i >= 5;
        const items = shiftsByDate[d] || [];
        const dayDrafts = draftsByDate[d] || [];
        return (
          <Cell
            key={d}
            date={d}
            employeeId={employee._id}
            employeeName={employee.name}
            shifts={items}
            drafts={dayDrafts}
            isWeekend={isWeekend}
            isToday={d === todayIso}
            templates={templates}
            onAssignTemplate={onAssignTemplate}
            onShiftClick={onShiftClick}
            onApproveDraft={onApproveDraft}
            onRejectDraft={onRejectDraft}
            approvingDraftId={approvingDraftId}
            selectedIds={selectedIds}
            onToggleSelect={onToggleSelect}
          />
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------
function Cell({
  date,
  employeeId,
  employeeName,
  shifts,
  drafts = [],
  isWeekend,
  isToday,
  templates,
  onAssignTemplate,
  onShiftClick,
  onApproveDraft,
  onRejectDraft,
  approvingDraftId,
  selectedIds,
  onToggleSelect,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);

  const onPick = async (tplId) => {
    setPickerOpen(false);
    await onAssignTemplate?.(tplId, employeeId, date);
  };

  return (
    <div
      className={cn(
        "relative border-b border-r border-slate-100 p-1.5 transition-colors dark:border-border/60",
        isWeekend && "bg-slate-50/60 dark:bg-foreground/[0.025]",
        isToday && "bg-indigo-50/30 dark:bg-indigo-500/[0.05]",
        pickerOpen && "bg-indigo-50/60 ring-2 ring-inset ring-indigo-400/70 dark:bg-indigo-500/15",
      )}
      data-testid={`cell-${employeeId}-${date}`}
    >
      <div className="relative flex min-h-[68px] flex-col gap-1">
        {shifts.map((sh) => (
          <ShiftBlock
            key={sh._id}
            shift={sh}
            selected={selectedIds?.has(sh._id)}
            onClick={(e) => {
              if (e.shiftKey || e.metaKey || e.ctrlKey) {
                onToggleSelect?.(sh._id);
                return;
              }
              onShiftClick?.(sh);
            }}
            onToggleSelect={() => onToggleSelect?.(sh._id)}
          />
        ))}
        {drafts.map((dr) => (
          <DraftBlock
            key={dr.id || dr._id}
            draft={dr}
            loading={approvingDraftId === (dr.id || dr._id)}
            onApprove={onApproveDraft}
            onReject={onRejectDraft}
          />
        ))}
        <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={cn(
                "group/empty grid w-full place-items-center rounded-md text-slate-300 transition-all hover:bg-slate-50/80 hover:text-slate-500 dark:text-muted-foreground/30 dark:hover:bg-foreground/[0.03] dark:hover:text-muted-foreground/60",
                shifts.length === 0 && drafts.length === 0
                  ? "h-full min-h-[40px]"
                  : "absolute inset-x-0 bottom-0 h-4 opacity-0 group-hover:opacity-100",
              )}
              aria-label="Műszak hozzáadása"
              data-testid={`add-shift-${employeeId}-${date}`}
            >
              <Plus
                className={cn(
                  "h-3.5 w-3.5 transition-opacity",
                  shifts.length === 0 && drafts.length === 0
                    ? "opacity-0 group-hover/empty:opacity-100"
                    : "opacity-30 group-hover/empty:opacity-80",
                )}
              />
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="start"
            side="bottom"
            sideOffset={6}
            className="w-72 p-0"
            data-testid="shift-picker-popover"
          >
            <TemplatePicker
              templates={templates}
              employeeName={employeeName}
              date={date}
              onPick={onPick}
            />
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function TemplatePicker({ templates, employeeName, date, onPick }) {
  const dt = new Date(date + "T00:00:00");
  const dayLabel = HU_DAY_LABELS[(dt.getDay() + 6) % 7].long;
  const dateLabel = `${String(dt.getMonth() + 1).padStart(2, "0")}. ${String(dt.getDate()).padStart(2, "0")}.`;

  return (
    <div className="overflow-hidden rounded-lg">
      <div className="border-b border-slate-100 bg-slate-50/60 px-3 py-2 dark:border-border dark:bg-foreground/[0.03]">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400 dark:text-muted-foreground">
          Műszak választás
        </p>
        <p className="mt-0.5 text-[12px] font-medium leading-tight text-slate-800 dark:text-foreground">
          {employeeName}
        </p>
        <p className="font-mono text-[10.5px] text-slate-500 dark:text-muted-foreground">
          {dayLabel} · {dateLabel}
        </p>
      </div>
      <div className="max-h-64 overflow-y-auto p-2">
        {templates.length === 0 ? (
          <div className="px-2 py-4 text-center">
            <p className="text-[11px] text-slate-500 dark:text-muted-foreground">
              Még nincs sablon ehhez a telephelyhez.
              <br />
              Hozz létre egyet a fenti „Sablonok kezelése&rdquo; gombbal.
            </p>
          </div>
        ) : (
          <div className="space-y-1">
            {templates.map((t) => {
              const cs = colorStyles(t.color);
              return (
                <button
                  key={t._id}
                  type="button"
                  onClick={() => onPick(t._id)}
                  className="group/pickerItem relative flex w-full items-center gap-2.5 overflow-hidden rounded-md border border-transparent bg-white px-2.5 py-2 text-left transition-all hover:border-slate-200 hover:bg-slate-50 hover:shadow-[0_4px_10px_-6px_rgba(15,23,42,0.18)] dark:bg-card dark:hover:border-border dark:hover:bg-foreground/[0.03]"
                  data-testid={`pick-template-${t._id}`}
                >
                  <span
                    className={cn("h-9 w-1 shrink-0 rounded-full", cs.dot)}
                    aria-hidden
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-[12.5px] font-semibold tracking-tight text-slate-900 dark:text-foreground">
                        {t.name}
                      </p>
                      <span className="font-mono text-[9.5px] font-semibold tabular-nums text-slate-500 dark:text-muted-foreground">
                        {fmtHours(t.duration_hours)}h
                      </span>
                    </div>
                    <p className="mt-0.5 font-mono text-[10.5px] tabular-nums text-slate-500 dark:text-muted-foreground">
                      <Clock className="-mt-0.5 mr-1 inline h-2.5 w-2.5 opacity-60" />
                      {t.start_time}–{t.end_time}
                      {t.unpaid_break_minutes > 0 && (
                        <span className="text-slate-400"> · -{t.unpaid_break_minutes}m szünet</span>
                      )}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function ShiftBlock({ shift, onClick, selected, onToggleSelect }) {
  const cs = colorStyles(shift.color);
  return (
    <div
      className={cn(
        "group/sh relative w-full overflow-hidden rounded-md border-[0.5px] shadow-[0_1px_2px_-1px_rgba(15,23,42,0.06)] transition-all hover:-translate-y-[0.5px] hover:shadow-[0_4px_10px_-4px_rgba(15,23,42,0.18)]",
        cs.block,
        selected && "ring-2 ring-indigo-500/70 ring-offset-1 ring-offset-background",
      )}
      data-testid={`shift-${shift._id}`}
    >
      {/* Left accent stripe */}
      <span
        className={cn("absolute inset-y-0 left-0 w-[3px] rounded-l-md", cs.dot)}
        aria-hidden
      />
      <button
        type="button"
        onClick={onClick}
        className="w-full py-1 pl-3 pr-2 text-left"
      >
        <div className="flex items-start justify-between gap-1.5">
          <p className="truncate text-[11.5px] font-semibold leading-tight tracking-tight">
            {shift.template_name || "Műszak"}
          </p>
          <span className="font-mono text-[9.5px] font-medium tabular-nums opacity-70">
            {fmtHours(shift.duration_hours)}h
          </span>
        </div>
        <p className="mt-0.5 font-mono text-[10px] font-medium uppercase tracking-wider tabular-nums opacity-65">
          {shift.start_time}–{shift.end_time}
        </p>
      </button>
      {/* Selection checkbox (top-right) */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggleSelect?.();
        }}
        className={cn(
          "absolute right-1 top-1 grid h-3.5 w-3.5 place-items-center rounded-[3px] border transition-all",
          selected
            ? "border-indigo-600 bg-indigo-600 opacity-100"
            : "border-current bg-background/60 opacity-0 group-hover/sh:opacity-70 hover:opacity-100",
        )}
        aria-label={selected ? "Kijelölés visszavonása" : "Kijelölés"}
        data-testid={`shift-select-${shift._id}`}
      >
        {selected && (
          <svg viewBox="0 0 12 12" className="h-2.5 w-2.5 stroke-white" strokeWidth={2.5} fill="none">
            <path d="M2 6 L5 9 L10 3" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </button>
      {/* Published indicator (left-bottom): tiny dot if published */}
      {shift.published && (
        <span
          className="absolute bottom-1 right-1 h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_0_2px_rgba(255,255,255,0.9)]"
          title="Publikálva"
          aria-label="Publikálva"
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
function Avatar({ name }) {
  const init = (name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("") || "—";
  // deterministic gradient based on first char
  const hue = (init.charCodeAt(0) * 23) % 360;
  return (
    <div
      className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-slate-200 text-[11px] font-semibold text-white shadow-sm"
      style={{
        background: `linear-gradient(135deg, hsl(${hue}deg 55% 38%), hsl(${(hue + 30) % 360}deg 60% 28%))`,
      }}
    >
      {init}
    </div>
  );
}

// ---------------------------------------------------------------------------
function HoursRing({ hours, testId }) {
  const value = Number(hours) || 0;
  // Cap progress at 40h baseline. Above 40h: ring full + amber accent.
  const target = 40;
  const pct = Math.min(value / target, 1);
  const isOvertime = value > target;
  // Megjelenítendő pontos érték (a "h" suffixet eldobjuk, hogy 3 számjegyű
  // vagy tizedes érték is beférjen a ringbe).
  const display = fmtHours(value); // pl. "47,3" vagy "8"
  // Font méret a karakter-szám alapján
  const fontClass =
    display.length >= 5
      ? "text-[7.5px]"
      : display.length === 4
        ? "text-[8.5px]"
        : "text-[10px]";
  const color = isOvertime ? "rgb(217 119 6)" : "rgb(79 70 229)";
  const trackColor = "rgba(15, 23, 42, 0.08)";
  const size = 36;
  const r = 14;
  const c = 2 * Math.PI * r;
  const dash = c * pct;

  return (
    <div
      className="relative flex shrink-0 items-center justify-center"
      data-testid={testId}
    >
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={trackColor}
          strokeWidth={3}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          stroke={color}
          strokeWidth={3}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${c}`}
          style={{ transition: "stroke-dasharray 0.4s ease" }}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center">
        <span
          className={cn(
            "font-mono font-semibold tabular-nums leading-none",
            fontClass,
            isOvertime
              ? "text-amber-600"
              : value > 0
                ? "text-slate-800 dark:text-foreground"
                : "text-slate-400 dark:text-muted-foreground",
          )}
          title={`${fmtHours(value)} óra`}
        >
          {display}
        </span>
      </div>
    </div>
  );
}


// ---------------------------------------------------------------------------
function DayInfoIcon({ stats }) {
  const hours = Number(stats?.hours) || 0;
  const revenue = Number(stats?.revenue) || 0;
  return (
    <TooltipProvider delayDuration={150}>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="absolute right-1.5 top-1.5 grid h-4 w-4 place-items-center rounded-full text-slate-300 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:text-muted-foreground/40 dark:hover:bg-foreground/[0.06] dark:hover:text-foreground"
            aria-label="Nap információk"
          >
            <Info className="h-3 w-3" strokeWidth={2} />
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="bottom"
          align="end"
          className="border-slate-200 bg-white px-0 py-0 text-foreground shadow-[0_12px_30px_-12px_rgba(15,23,42,0.35)] dark:border-border dark:bg-card"
        >
          <div className="min-w-[180px] divide-y divide-slate-100 dark:divide-border/60">
            <div className="flex items-center gap-2 px-3 py-2">
              <div className="grid h-7 w-7 place-items-center rounded-lg bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15">
                <Clock className="h-3.5 w-3.5" strokeWidth={2} />
              </div>
              <div className="flex-1">
                <p className="text-[9.5px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Beosztott órák
                </p>
                <p className="font-mono text-[13px] font-semibold tabular-nums">
                  {fmtHours(hours)} óra
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-2">
              <div className="grid h-7 w-7 place-items-center rounded-lg bg-emerald-50 text-emerald-700 dark:bg-emerald-500/15">
                <Coins className="h-3.5 w-3.5" strokeWidth={2} />
              </div>
              <div className="flex-1">
                <p className="text-[9.5px] font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Napi bevétel
                </p>
                <p className="font-mono text-[13px] font-semibold tabular-nums">
                  {revenue.toLocaleString("hu-HU", { maximumFractionDigits: 0 })} Ft
                </p>
              </div>
            </div>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
